/// <reference path="../../typings/browser.d.ts" />

namespace Application.Models {
    'use strict';

    export type MONOTONOUS_TYPE = "INCREASING" | "DECREASING" | "EQUALS";
    const MAX_INTERPOLATION_BIAS = 0.03;


    export class Interpolate {
        private points: number[][];
        
        constructor(points: number[][]) {
            this.points = points;
        }

        public getLinearInterpolation(startPoint: number, endPoint: number, interval: number): number[][] {
            let result: number[][] = [];
            if (this.points.length < 2) {
                return [];
            } else {
                let x0: number = this.points[0][0],
                    y0: number = this.points[0][1],
                    x1: number = this.points[1][0],
                    y1: number = this.points[1][1];
                let f: (num: number) => number = (x: number) => {
                    return y0 + (y1 - y0) / (x1 - x0) * (x - x0);
                };

                for (let i: number = startPoint; i <= startPoint + endPoint; i += interval) {
                    result.push([i, f(i)]);
                }
                return result;
            }
        }

        public getCuadraticInterpolation(startPoint: number, endPoint: number, interval: number): number[][] {
            let result: number[][] = [];
            if (this.points.length < 3) {
                return [];
            } else {
                let x0: number = this.points[0][0],
                    y0: number = this.points[0][1],
                    x1: number = this.points[1][0],
                    y1: number = this.points[1][1],
                    x2: number = this.points[2][0],
                    y2: number = this.points[2][1];
                
                let f: (num: number) => number = (x: number) => {
                    return y0 * (((x - x1) * (x - x2)) / ((x0 - x1) * (x0 - x2))) + 
                           y1 * (((x - x0) * (x - x2)) / ((x1 - x0) * (x1 - x2))) +
                           y2 * (((x - x0) * (x - x1)) / ((x2 - x0) * (x2 - x1)));
                };

                for (let i: number = startPoint; i <= startPoint + endPoint; i += interval) {
                    let n: number = f(i);
                    if (isFinite(n)) {
                        n = n < 0 ? 0 : n;
                        result.push([i, n]);
                    }
                }
            }
            return result;
        }

        public spline(x: number, xs: number[], ys: number[], monotonousType: MONOTONOUS_TYPE) {
            let previousX: number = 0;
            xs.forEach((x, index) => {
                if (x - previousX < 10) {
                    xs.splice(index - 1, 1);
                    ys.splice(index - 1, 1);
                }
                previousX = x;
            });

            let ks: number[] = xs.map(() => 0);
            ks = this.getNaturalKs(xs, ys, ks);
            let i: number = 1;
            while(xs[i]<x) i++;
            const nextY: number = i < ys.length ? ys[i] : undefined;
            const t: number = (x - xs[i-1]) / (xs[i] - xs[i-1]);
            const a: number =  ks[i-1]*(xs[i]-xs[i-1]) - (ys[i]-ys[i-1]);
            const b: number = -ks[i]*(xs[i]-xs[i-1]) + (ys[i]-ys[i-1]);
            let q: number = (1-t)*ys[i-1] + t*ys[i] + t*(1-t)*(a*(1-t)+b*t);
            // console.log(`Next y ${nextY} type ${monotonousType} Calculated q ${q}`);
            if (monotonousType && nextY != undefined) {
                switch (monotonousType) {
                    case "INCREASING":
                        if (q > (nextY + MAX_INTERPOLATION_BIAS)) {
                            q = nextY + MAX_INTERPOLATION_BIAS;
                        }
                        break;
                    case "DECREASING":
                        if (q < (nextY - MAX_INTERPOLATION_BIAS)) {
                            q = Math.max(0, nextY - MAX_INTERPOLATION_BIAS);
                        }
                        break;
                    case "EQUALS":
                        if (q !== nextY)
                            q = nextY;
                        break;
                }
            }

            return q;
        }


        private getNaturalKs(xs: number[], ys: number[], ks: number[]): number[] {
            const n: number = xs.length-1;
            const A: number[][] = this.zerosMat(n+1, n+2);

            for(let i=1; i<n; i++) {
                A[i][i-1] = 1/(xs[i] - xs[i-1]);
                A[i][i] = 2 * (1/(xs[i] - xs[i-1]) + 1/(xs[i+1] - xs[i])) ;
                A[i][i+1] = 1/(xs[i+1] - xs[i]);
                A[i][n+1] = 3*( (ys[i]-ys[i-1])/((xs[i] - xs[i-1])*(xs[i] - xs[i-1]))  +  (ys[i+1]-ys[i])/ ((xs[i+1] - xs[i])*(xs[i+1] - xs[i])) );
            }

            A[0][0] = 2/(xs[1] - xs[0]);
            A[0][1] = 1/(xs[1] - xs[0]);
            A[0][n+1] = 3 * (ys[1] - ys[0]) / ((xs[1]-xs[0])*(xs[1]-xs[0]));

            A[n][n-1] = 1/(xs[n] - xs[n-1]);
            A[n][n] = 2/(xs[n] - xs[n-1]);
            A[n][n+1] = 3 * (ys[n] - ys[n-1]) / ((xs[n]-xs[n-1])*(xs[n]-xs[n-1]));

            return this.solve(A, ks);
        }


        private solve(A: number[][], ks: number[]): number[] {
            const m: number = A.length;
            for(let k=0; k<m; k++) {// column
                // pivot for column
                let i_max: number = 0; let vali: number = Number.NEGATIVE_INFINITY;
                for(let i=k; i<m; i++) if(A[i][k]>vali) { i_max = i; vali = A[i][k];}
                this.swapRows(A, k, i_max);

                // for all rows below pivot
                for(let i=k+1; i<m; i++) {
                    for(let j=k+1; j<m+1; j++)
                        A[i][j] = A[i][j] - A[k][j] * (A[i][k] / A[k][k]);
                    A[i][k] = 0;
                }
            }
            for(let i=m-1; i>=0; i--) {// rows = columns
                const v: number = A[i][m] / A[i][i];
                ks[i] = v;
                for(let j=i-1; j>=0; j--){ // rows
                    A[j][m] -= A[j][i] * v;
                    A[j][i] = 0;
                }
            }
            return ks;
        }

        private zerosMat(r: number,c: number): number[][] {
            const A: number[][] = [];
            for(let i=0; i<r; i++) {
                A.push([]);
                for(let j=0; j<c; j++) A[i].push(0);
            }
            return A;
        }

        private swapRows (m: number[][], k: number, l: number): void {
            const p: number[] = m[k]; m[k] = m[l]; m[l] = p;
        }
    }
}






    